import React, { useEffect, useState, useMemo } from "react";
import API from "@/lib/api";
import { format, differenceInDays, startOfMonth, addMonths, isBefore, isAfter, max } from "date-fns";
import { Loader2, Calendar, Download, TrendingUp, AlertTriangle, CheckCircle2, Search, Users } from "lucide-react";

const PIXELS_PER_DAY = 4;
const DAY_IN_MS = 1000 * 60 * 60 * 24;

const STATUS_COLORS = {
  todo: "bg-slate-400",
  in_progress: "bg-blue-500",
  review: "bg-yellow-400",
  done: "bg-emerald-500",
};

const getProgress = (status) => {
  if (status === 'done') return '100%';
  if (status === 'review') return '75%';
  if (status === 'in_progress') return '50%';
  return '0%';
};

const PROJECT_COLORS = [
  "bg-indigo-50 dark:bg-indigo-900/20",
  "bg-pink-50 dark:bg-pink-900/20",
  "bg-emerald-50 dark:bg-emerald-900/20",
  "bg-amber-50 dark:bg-amber-900/20",
  "bg-cyan-50 dark:bg-cyan-900/20",
  "bg-violet-50 dark:bg-violet-900/20",
  "bg-rose-50 dark:bg-rose-900/20",
  "bg-teal-50 dark:bg-teal-900/20",
  "bg-fuchsia-50 dark:bg-fuchsia-900/20",
  "bg-sky-50 dark:bg-sky-900/20",
  "bg-orange-50 dark:bg-orange-900/20",
  "bg-lime-50 dark:bg-lime-900/20",
  "bg-blue-50 dark:bg-blue-900/20",
  "bg-green-50 dark:bg-green-900/20",
  "bg-yellow-50 dark:bg-yellow-900/20",
  "bg-red-50 dark:bg-red-900/20",
  "bg-purple-50 dark:bg-purple-900/20",
  "bg-zinc-50 dark:bg-zinc-900/20",
  "bg-slate-50 dark:bg-slate-900/20",
  "bg-stone-50 dark:bg-stone-900/20"
];

const PROJECT_DOT_COLORS = [
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-violet-500",
  "bg-rose-500",
  "bg-teal-500",
  "bg-fuchsia-500",
  "bg-sky-500",
  "bg-orange-500",
  "bg-lime-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-red-500",
  "bg-purple-500",
  "bg-zinc-500",
  "bg-slate-500",
  "bg-stone-500"
];

export default function RoadmapPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [customStartMonth, setCustomStartMonth] = useState("");
  const [customEndMonth, setCustomEndMonth] = useState("");

  const currentPixelsPerDay = 32;

  useEffect(() => {
    const fetchRoadmap = async () => {
      try {
        const { data } = await API.get("/roadmap");
        setProjects(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRoadmap();
  }, []);

  const uniqueAssignees = useMemo(() => {
    const assignees = new Set();
    projects.forEach(p => {
      p.tasks.forEach(t => {
        if (t.assignee_name) assignees.add(t.assignee_name);
      });
    });
    return Array.from(assignees).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.map(p => {
      let matchingTasks = p.tasks;

      if (searchQuery.trim()) {
        const lowerQuery = searchQuery.toLowerCase();
        if (!p.name.toLowerCase().includes(lowerQuery)) {
          matchingTasks = matchingTasks.filter(t => 
            t.title.toLowerCase().includes(lowerQuery) || 
            (t.assignee_name && t.assignee_name.toLowerCase().includes(lowerQuery))
          );
        }
      }

      if (selectedAssignee) {
        matchingTasks = matchingTasks.filter(t => t.assignee_name === selectedAssignee);
      }
      
      return matchingTasks.length > 0 ? { ...p, tasks: matchingTasks } : null;
    }).filter(Boolean);
  }, [projects, searchQuery, selectedAssignee]);

  const timelineConfig = useMemo(() => {
    if (!filteredProjects.length && !customStartMonth && !customEndMonth) return { startDate: new Date(), months: [] };

    let minDate = new Date();
    let maxDate = new Date();
    let hasDates = false;

    // Default timeline goal: Start from the oldest UNFINISHED task, up to TODAY.
    filteredProjects.forEach(p => {
      p.tasks.forEach(t => {
        // Only consider tasks that are NOT done
        if (t.status !== 'done') {
          t.segments.forEach(s => {
            const start = new Date(s.start);
            // We only care about the earliest start date among unfinished tasks
            if (!hasDates || isBefore(start, minDate)) {
              minDate = start;
              hasDates = true;
            }
          });
        }
      });
    });

    if (!hasDates) {
      // Fallback: If all tasks are done or no tasks exist, just show current month and 1 month ahead
      minDate = new Date();
      maxDate = addMonths(new Date(), 1);
    } else {
      // Set the maxDate exactly to TODAY
      maxDate = new Date();
      
      // Ensure minDate is not after maxDate (just in case)
      if (isAfter(minDate, maxDate)) {
        minDate = addMonths(maxDate, -1);
      }
      
      // Add padding: 1 month before the earliest task, and 1 month after today
      minDate = addMonths(minDate, -1);
      maxDate = addMonths(maxDate, 1); 
    }

    // Override with custom dates if provided
    if (customStartMonth) {
      minDate = new Date(customStartMonth + "-01T00:00:00");
    }
    if (customEndMonth) {
      // Make it the end of the selected month
      maxDate = new Date(customEndMonth + "-01T00:00:00");
      maxDate = addMonths(maxDate, 1); // add 1 month to include the end month fully
    }

    // Ensure min is before max
    if (isAfter(minDate, maxDate)) {
       maxDate = addMonths(minDate, 1);
    }

    const startDate = startOfMonth(minDate);
    const months = [];
    let currentMonth = startDate;
    while (isBefore(currentMonth, maxDate)) {
      months.push(currentMonth);
      currentMonth = addMonths(currentMonth, 1);
    }

    return { startDate, months };
  }, [filteredProjects, customStartMonth, customEndMonth]);

  // Calculate total timeline width in pixels
  const totalTimelineWidth = useMemo(() => {
    if (!timelineConfig.months.length) return 0;
    let totalDays = 0;
    timelineConfig.months.forEach(m => {
      totalDays += differenceInDays(addMonths(m, 1), m);
    });
    return totalDays * currentPixelsPerDay;
  }, [timelineConfig, currentPixelsPerDay]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const getPositionStyle = (startStr, endStr) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const leftDays = differenceInDays(start, timelineConfig.startDate);
    const widthDays = Math.max(1, differenceInDays(end, start));
    return {
      left: `${leftDays * currentPixelsPerDay}px`,
      width: `${widthDays * currentPixelsPerDay}px`
    };
  };

  const getOverallStats = () => {
    let active = 0, completed = 0, review = 0;
    filteredProjects.forEach(p => {
      p.tasks.forEach(t => {
        if (t.status === 'done') completed++;
        else if (t.status === 'review') review++;
        else active++;
      });
    });
    const total = active + completed + review;
    const velocity = total === 0 ? 0 : Math.round((completed / total) * 100);
    return { active, completed, review, velocity };
  };

  const stats = getOverallStats();

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Main Canvas */}
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        {/* Page Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Roadmap Proyek</h2>
            <p className="text-slate-500 dark:text-slate-400 font-medium">Garis waktu durasi tugas & riwayat status</p>
          </div>
          <div className="flex items-center flex-wrap gap-3">
            {/* Search Filter */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Cari tugas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-48 transition-all dark:text-slate-200 dark:placeholder-slate-500"
              />
            </div>
            
            {/* Assignee Filter */}
            <div className="relative flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl transition-all hover:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/20">
              <div className="pl-3 flex items-center justify-center pointer-events-none">
                <Users className="w-4 h-4 text-slate-400 dark:text-slate-500" />
              </div>
              <select
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                className="pl-2 pr-8 py-2 bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 focus:outline-none appearance-none cursor-pointer"
              >
                <option value="" className="dark:bg-slate-800">Semua Anggota</option>
                {uniqueAssignees.map(name => (
                  <option key={name} value={name} className="dark:bg-slate-800">{name}</option>
                ))}
              </select>
            </div>

            <div className="bg-white dark:bg-slate-800 p-1 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 flex items-center">
              <div className="flex items-center px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors group relative">
                <Calendar className="text-indigo-600 dark:text-indigo-400 w-4 h-4 mr-2 pointer-events-none" />
                {/* Fallback styling for Safari/iOS */}
                <input 
                  type="month" 
                  value={customStartMonth || (timelineConfig.months.length > 0 ? format(timelineConfig.months[0], 'yyyy-MM') : '')}
                  onChange={(e) => setCustomStartMonth(e.target.value)}
                  className="text-sm font-bold text-slate-700 dark:text-slate-200 bg-transparent border-none p-0 focus:ring-0 cursor-pointer outline-none w-[130px] dark:[color-scheme:dark]"
                />
              </div>
              <span className="text-slate-300 dark:text-slate-600 font-black px-1">→</span>
              <div className="flex items-center px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors group relative">
                <input 
                  type="month" 
                  value={customEndMonth || (timelineConfig.months.length > 0 ? format(timelineConfig.months[timelineConfig.months.length - 1], 'yyyy-MM') : '')}
                  onChange={(e) => setCustomEndMonth(e.target.value)}
                  className="text-sm font-bold text-slate-700 dark:text-slate-200 bg-transparent border-none p-0 focus:ring-0 cursor-pointer outline-none w-[130px] dark:[color-scheme:dark]"
                />
              </div>
              {(customStartMonth || customEndMonth) && (
                <button 
                  onClick={() => { setCustomStartMonth(''); setCustomEndMonth(''); }}
                  className="ml-1 mr-2 text-[10px] bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 px-2 py-1 rounded-md font-bold transition-colors"
                  title="Atur Ulang ke Auto-fit"
                >
                  RESET
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Roadmap Container */}
        <div id="roadmap-canvas" className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
          <div className="flex overflow-x-auto">
            {/* Fixed Left Sidebar: Task Names */}
            <div className="w-[450px] flex-shrink-0 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10 sticky left-0 shadow-[4px_0_10px_rgba(0,0,0,0.05)] dark:shadow-[4px_0_10px_rgba(0,0,0,0.2)]">
              <div className="flex items-center px-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 h-[72px]">
                <div className="grid grid-cols-[1fr_50px_60px_60px] gap-2 w-full">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Tugas</span>
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-center">Prog</span>
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-center">Mulai</span>
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-center">Selesai</span>
                </div>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {filteredProjects.map((proj, pIdx) => {
                  const bgClass = PROJECT_COLORS[pIdx % PROJECT_COLORS.length];
                  const dotClass = PROJECT_DOT_COLORS[pIdx % PROJECT_DOT_COLORS.length];
                  return (
                    <div key={proj.name} className={`p-4 ${bgClass} bg-opacity-40 border-b border-white/40 dark:border-slate-700/40`}>
                      <div className="flex items-center gap-3 mb-3 h-[24px]">
                        <div className={`w-2.5 h-2.5 rounded-full ${dotClass}`}></div>
                        <h3 className="font-bold text-slate-900 dark:text-white">{proj.name}</h3>
                      </div>
                      <div className="space-y-0">
                        {proj.tasks.map(task => {
                          const isDone = task.status === 'done';
                          
                          // Find true start and end dates from segments
                          let start = null;
                          let end = null;
                          if (task.segments && task.segments.length > 0) {
                            start = new Date(task.segments[0].start);
                            end = new Date(task.segments[task.segments.length - 1].end);
                          }

                          return (
                            <div key={task.id} className="h-[48px] grid grid-cols-[1fr_50px_60px_60px] gap-2 items-center text-sm border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                              <div className="min-w-0 pr-2">
                                <span className={`block font-medium ${isDone ? 'text-slate-400 dark:text-slate-500 line-through' : 'text-slate-700 dark:text-slate-200'} truncate`}>
                                  {task.title}
                                </span>
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate block mt-0.5">{task.assignee_name}</span>
                              </div>
                              <div className="text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                                {getProgress(task.status)}
                              </div>
                              <div className="text-center text-[10px] text-slate-500 dark:text-slate-400">
                                {start ? format(start, 'M/d/yy') : '-'}
                              </div>
                              <div className="text-center text-[10px] text-slate-500 dark:text-slate-400">
                                {isDone && end ? format(end, 'M/d/yy') : '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side: Timeline Grid */}
            <div className="flex-1 relative flex flex-col min-w-0 overflow-x-auto">
              {/* Timeline Header (Months & Days) */}
              <div className="flex flex-col bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700" style={{ width: `${totalTimelineWidth}px` }}>
                {/* Months Row */}
                <div className="h-10 flex">
                  {timelineConfig.months.map((m, i) => {
                    const daysInMonth = differenceInDays(addMonths(m, 1), m);
                    const width = daysInMonth * currentPixelsPerDay;
                    return (
                      <div key={i} style={{ width: `${width}px`, minWidth: `${width}px` }} className="flex-shrink-0 border-r border-slate-200 dark:border-slate-700 flex items-center justify-center">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">{format(m, 'MMMM yyyy')}</span>
                      </div>
                    );
                  })}
                </div>
                {/* Days Row */}
                <div className="h-8 flex border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  {timelineConfig.months.map((m, i) => {
                    const daysInMonth = differenceInDays(addMonths(m, 1), m);
                    return Array.from({ length: daysInMonth }).map((_, dIdx) => (
                      <div key={`${i}-${dIdx}`} style={{ width: `${currentPixelsPerDay}px`, minWidth: `${currentPixelsPerDay}px` }} className="flex-shrink-0 border-r border-slate-100 dark:border-slate-700/50 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">{dIdx + 1}</span>
                      </div>
                    ));
                  })}
                </div>
              </div>
              
              {/* Timeline Body */}
              <div className="flex-1 relative min-h-[500px]" style={{
                width: `${totalTimelineWidth}px`,
                backgroundImage: 'linear-gradient(to right, rgba(148, 163, 184, 0.1) 1px, transparent 1px)',
                backgroundSize: `${currentPixelsPerDay}px 100%`
              }}>
                {/* Today Marker */}
                {(() => {
                  const today00 = new Date();
                  today00.setHours(0, 0, 0, 0);
                  const tlStart = new Date(timelineConfig.startDate);
                  tlStart.setHours(0, 0, 0, 0);
                  
                  // Position the line at the exact center of today's column (difference + 0.5)
                  const leftPos = Math.max(0, (differenceInDays(today00, tlStart) + 0.5) * currentPixelsPerDay);
                  
                  return (
                    <div 
                      className="absolute top-0 bottom-0 w-px bg-red-500 dark:bg-red-400 z-20 flex flex-col items-center"
                      style={{ left: `${leftPos}px` }}
                    >
                      <div className="bg-red-500 dark:bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full -mt-2 shadow-sm">HARI INI</div>
                    </div>
                  );
                })()}

                {/* Task Bars Content */}
                <div className="divide-y divide-slate-100/0 dark:divide-slate-700/0">
                  {filteredProjects.map((proj, pIdx) => {
                    const dotClass = PROJECT_DOT_COLORS[pIdx % PROJECT_DOT_COLORS.length];
                    return (
                      <div key={proj.name} className="p-4 border-b border-slate-200/50 dark:border-slate-700/50">
                        {/* Empty space matching the project header height on the left */}
                        <div className="h-[24px] mb-3"></div>
                        <div className="space-y-0">
                          {proj.tasks.map(task => {
                            let start = null;
                            let end = null;
                            const isDone = task.status === 'done';

                            if (task.segments && task.segments.length > 0) {
                              start = task.segments[0].start;
                              if (isDone) {
                                // Jika selesai, balok berhenti TEPAT di tanggal akhir keseluruhan (end date terakhir)
                                // yang mana seharusnya identik dengan tanggal kolom END di tabel sebelah kiri
                                end = task.segments[task.segments.length - 1].end;
                              } else {
                                // Jika belum selesai, panjangkan hanya sampai HARI INI saja
                                end = new Date().toISOString();
                              }
                            }

                            return (
                              <div key={task.id} className="h-[48px] relative border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                                {start && end && (() => {
                                  // Strip time to avoid differenceInDays hour-truncation bugs
                                  const sDate = new Date(start);
                                  sDate.setHours(0, 0, 0, 0);
                                  
                                  const eDate = new Date(end);
                                  eDate.setHours(0, 0, 0, 0);
                                  
                                  const tlStart = new Date(timelineConfig.startDate);
                                  tlStart.setHours(0, 0, 0, 0);

                                  const leftDays = differenceInDays(sDate, tlStart);
                                  // Add +1 to make sure the bar includes the end date column entirely
                                  const widthDays = Math.max(1, differenceInDays(eDate, sDate) + 1);
                                  
                                  const style = {
                                    left: `${leftDays * currentPixelsPerDay}px`,
                                    width: `${widthDays * currentPixelsPerDay}px`
                                  };

                                  return (
                                    <div 
                                      className="absolute top-1/2 -translate-y-1/2 h-5 rounded-full overflow-hidden flex group cursor-pointer transition-all hover:scale-[1.01] hover:shadow-md z-10 hover:z-20"
                                      style={style}
                                      title={`${task.title} - ${task.status.replace('_', ' ')} (${format(sDate, 'MMM d')} to ${format(eDate, 'MMM d')})`}
                                    >
                                      {/* Faded Background (Before End Date) */}
                                      <div className={`flex-1 ${dotClass} opacity-30 h-full`}></div>
                                      
                                      {/* Solid Foreground (Only exactly at End Date if Done) */}
                                      {isDone && (
                                        <div 
                                          className={`${dotClass} opacity-100 h-full flex-shrink-0`} 
                                          style={{ width: `${currentPixelsPerDay}px` }}
                                        ></div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Stats */}
        <div className="mt-8 px-8 py-6 border border-slate-200 dark:border-slate-700 rounded-xl flex flex-wrap items-center justify-between bg-white dark:bg-slate-800 shadow-sm gap-4">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-slate-500 opacity-100"></span>
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Progres Selesai</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-slate-400 dark:bg-slate-600 opacity-30 border border-slate-500 dark:border-slate-400"></span>
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Sisa</span>
            </div>
            <span className="text-xs font-medium text-slate-400 dark:text-slate-500 italic border-l border-slate-200 dark:border-slate-700 pl-4">
              * Warna balok menunjukkan kategori Proyek
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Kecepatan Keseluruhan</span>
              <div className="w-32 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-600 dark:bg-indigo-500 rounded-full" style={{ width: `${stats.velocity}%` }}></div>
              </div>
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{stats.velocity}%</span>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}
